import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import { captureError } from '../../../utils/sentryHelpers';
import { pickSourceText } from '../../../utils/slugify';
import {
  extractSlugLookupItems,
  findSlugOwner,
  resolveRequiredSlug,
  slugHistoryMutationInput,
} from '../../../utils/slugInput';
import {
  DEFAULT_MEDIA_STATUS,
  buildMediaSeriesInput,
  compareMediaItems,
  connectionHasItems,
  isSeriesTypeLocked,
  moveItem,
  reorderSortKeys,
  setLocalized,
  validateMediaSeries,
} from '../../../utils/mediathek';
import {
  CREATE_MEDIA_SERIES,
  DELETE_MEDIA_SERIES,
  GET_MEDIA_SERIES,
  LIST_INDUSTRIES,
  MEDIA_ITEMS_BY_MEDIA_SERIES,
  MEDIA_ITEM_IDS_BY_MEDIA_SERIES,
  MEDIA_SERIES_BY_SLUG,
  UPDATE_MEDIA_ITEM_SORT_KEY,
  UPDATE_MEDIA_SERIES,
  fetchAllPages,
} from '../mediathekGraphql';

const client = generateClient();
const COMPONENT = 'useMediaSeriesData';
/** Parallel updateMediaItem calls while writing the new order */
const REORDER_BATCH_SIZE = 5;
/** Error of a failed items load (i18n key, translated by ErrorList); removed again before every new attempt */
const ITEMS_LOAD_ERROR = 'The items of this series could not be loaded';

const emptySeries = () => ({
  type: 'podcast',
  title: [],
  slug: '',
  slugHistory: [],
  excerpt: [],
  description: [],
  host: '',
  coverImage: null,
  status: DEFAULT_MEDIA_STATUS,
  sortKey: '',
  // undefined = not chosen yet (new series default to the "cp" industry once industries are loaded)
  industryId: undefined,
});

/** Loaded record -> form state (inputs need strings instead of null). */
function toFormState(series) {
  return {
    ...series,
    title: series.title || [],
    slug: series.slug || '',
    slugHistory: series.slugHistory || [],
    excerpt: series.excerpt || [],
    description: series.description || [],
    host: series.host || '',
    status: series.status || DEFAULT_MEDIA_STATUS,
    sortKey: series.sortKey === null || series.sortKey === undefined ? '' : String(series.sortKey),
    industryId: series.industryId || null,
  };
}

function reportError(error, operation, context) {
  console.error(`Mediathek series: ${operation} failed`, error);
  captureError(error, { feature: 'mediathek', operation, context, tags: { component: COMPONENT } });
}

/**
 * Data + actions of the series edit page (create when seriesId is empty).
 * @param {string|undefined} seriesId
 */
export default function useMediaSeriesData(seriesId) {
  const isUpdate = Boolean(seriesId);
  const [series, setSeries] = useState(emptySeries);
  // As loaded: "slug"/"slugHistory" are maintained by this form (no stream lambda) and the
  // type is locked while the series has items
  const [loadedSlug, setLoadedSlug] = useState(null);
  const [loadedHistory, setLoadedHistory] = useState([]);
  const [loadedType, setLoadedType] = useState(null);
  const [items, setItems] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [isLoading, setIsLoading] = useState(isUpdate);
  const [isLoadingItems, setIsLoadingItems] = useState(isUpdate);
  // true when the last item load failed: then it is unknown whether the series has items
  const [itemsLoadFailed, setItemsLoadFailed] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isReordering, setIsReordering] = useState(false);
  const [errors, setErrors] = useState([]);
  // Bumped after loading so the rich text editors re-initialise with the loaded HTML
  const [loadVersion, setLoadVersion] = useState(0);
  const industryTouched = useRef(false);

  const fetchSeries = useCallback(async () => {
    if (!seriesId) return;
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await client.graphql({ query: GET_MEDIA_SERIES, variables: { id: seriesId } });
      const loaded = response?.data?.getMediaSeries;
      if (!loaded) {
        setLoadError('The series was not found');
        return;
      }
      setSeries(toFormState(loaded));
      setLoadedSlug(loaded.slug ?? null);
      setLoadedHistory(loaded.slugHistory || []);
      setLoadedType(loaded.type ?? null);
      setLoadVersion((v) => v + 1);
    } catch (error) {
      reportError(error, 'fetch-series', { seriesId });
      setLoadError('The series could not be loaded');
    } finally {
      setIsLoading(false);
    }
  }, [seriesId]);

  const fetchItems = useCallback(async () => {
    if (!seriesId) return;
    setIsLoadingItems(true);
    setItemsLoadFailed(false);
    // Drop the message of a previous failed attempt: a successful retry leaves no stale error, a failed one does not add it twice
    setErrors((prev) => prev.filter((e) => e !== ITEMS_LOAD_ERROR));
    try {
      const all = await fetchAllPages(client, MEDIA_ITEMS_BY_MEDIA_SERIES, { mediaSeriesId: seriesId }, 'mediaItemsByMediaSeries');
      setItems(all.sort(compareMediaItems));
    } catch (error) {
      reportError(error, 'fetch-series-items', { seriesId });
      setItemsLoadFailed(true);
      setErrors((prev) => [...prev, ITEMS_LOAD_ERROR]);
    } finally {
      setIsLoadingItems(false);
    }
  }, [seriesId]);

  const fetchIndustries = useCallback(async () => {
    try {
      const all = await fetchAllPages(client, LIST_INDUSTRIES, {}, 'listIndustries');
      setIndustries(all);
    } catch (error) {
      reportError(error, 'fetch-industries', {});
    }
  }, []);

  useEffect(() => {
    fetchIndustries();
  }, [fetchIndustries]);

  useEffect(() => {
    if (seriesId) {
      fetchSeries();
      fetchItems();
    } else {
      setSeries(emptySeries());
      setItems([]);
      setLoadedSlug(null);
      setLoadedHistory([]);
      setLoadedType(null);
      setIsLoading(false);
      setIsLoadingItems(false);
      setItemsLoadFailed(false);
    }
  }, [seriesId, fetchSeries, fetchItems]);

  // New series: default industry = "cp" (when it exists and nothing was chosen yet)
  useEffect(() => {
    if (isUpdate || industryTouched.current || industries.length === 0) return;
    setSeries((prev) => {
      if (prev.industryId !== undefined) return prev;
      const cp = industries.find((industry) => industry.handle === 'cp');
      return { ...prev, industryId: cp ? cp.id : null };
    });
  }, [industries, isUpdate]);

  const setField = useCallback((field, value) => {
    if (field === 'industryId') industryTouched.current = true;
    setSeries((prev) => ({ ...prev, [field]: value }));
  }, []);

  const setLocalizedField = useCallback((field, language, text) => {
    setSeries((prev) => ({ ...prev, [field]: setLocalized(prev[field], language, text) }));
  }, []);

  const hasItems = items.length > 0;
  const typeLocked = isSeriesTypeLocked({ hasItems, isLoadingItems, itemsLoadFailed });

  /** true when another series already uses the slug (throws when the lookup fails). */
  const isSlugTaken = useCallback(async (slug) => {
    let lookupItems;
    try {
      const response = await client.graphql({ query: MEDIA_SERIES_BY_SLUG, variables: { slug, limit: 50 } });
      lookupItems = extractSlugLookupItems(response, 'mediaSeries');
    } catch (error) {
      // A rejected GraphQLResult may still carry the items (partial data); otherwise save() reports it
      lookupItems = extractSlugLookupItems(error, 'mediaSeries');
      if (!lookupItems) throw error;
      reportError(error, 'slug-lookup-partial', { slug });
    }
    return findSlugOwner(lookupItems || [], seriesId, 'mediaSeries') !== null;
  }, [seriesId]);

  /**
   * Validates and saves. Returns { id, slug } on success, null otherwise
   * (errors are in `errors`).
   */
  const save = useCallback(async () => {
    // The loaded list may be stale (items created in another tab) or missing (load failed):
    // a type change on an update is checked against the backend before it is allowed
    let itemsExist = hasItems;
    if (isUpdate && loadedType && series.type !== loadedType && !hasItems) {
      try {
        const check = await client.graphql({
          query: MEDIA_ITEM_IDS_BY_MEDIA_SERIES,
          variables: { mediaSeriesId: seriesId, limit: 1 },
        });
        itemsExist = connectionHasItems(check?.data?.mediaItemsByMediaSeries);
      } catch (error) {
        reportError(error, 'check-series-items', { seriesId });
        setErrors(['The type cannot be changed because the items of this series could not be checked']);
        return null;
      }
    }
    const validation = validateMediaSeries(series, { hasItems: itemsExist, loadedType: isUpdate ? loadedType : null });
    if (validation.length) {
      setErrors(validation);
      return null;
    }
    setErrors([]);
    setIsSaving(true);
    try {
      const slugResult = await resolveRequiredSlug(series.slug, {
        loadedSlug,
        sourceText: pickSourceText(series.title),
        fallback: 'series',
        isTaken: isSlugTaken,
      });
      if (slugResult.conflict) {
        setErrors(['This slug is already used by another series']);
        return null;
      }
      const input = {
        ...buildMediaSeriesInput({ ...series, id: seriesId }, { isUpdate }),
        slug: slugResult.slug,
        ...(isUpdate ? slugHistoryMutationInput(slugResult.slug, loadedSlug, loadedHistory) : {}),
      };
      const response = await client.graphql({
        query: isUpdate ? UPDATE_MEDIA_SERIES : CREATE_MEDIA_SERIES,
        variables: { input },
      });
      const saved = isUpdate ? response?.data?.updateMediaSeries : response?.data?.createMediaSeries;
      if (!saved?.id) throw new Error('The series could not be saved');
      setSeries((prev) => ({ ...prev, id: saved.id, slug: saved.slug ?? slugResult.slug, slugHistory: saved.slugHistory || [] }));
      setLoadedSlug(saved.slug ?? slugResult.slug);
      setLoadedHistory(saved.slugHistory || []);
      setLoadedType(series.type);
      return { id: saved.id, slug: saved.slug ?? slugResult.slug };
    } catch (error) {
      reportError(error, isUpdate ? 'update-series' : 'create-series', { seriesId });
      setErrors([error?.errors?.[0]?.message || error?.message || 'The series could not be saved']);
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [series, hasItems, isUpdate, loadedType, loadedSlug, loadedHistory, seriesId, isSlugTaken]);

  /**
   * Deletes the series. Refuses while it still has items (checked against the
   * backend, not only the loaded list). Returns true on success.
   */
  const deleteSeries = useCallback(async () => {
    if (!seriesId) return false;
    setErrors([]);
    try {
      const check = await client.graphql({
        query: MEDIA_ITEM_IDS_BY_MEDIA_SERIES,
        variables: { mediaSeriesId: seriesId, limit: 1 },
      });
      if (connectionHasItems(check?.data?.mediaItemsByMediaSeries)) {
        setErrors(['This series still has items. Delete them or move them to another series first.']);
        await fetchItems();
        return false;
      }
      await client.graphql({ query: DELETE_MEDIA_SERIES, variables: { input: { id: seriesId } } });
      return true;
    } catch (error) {
      reportError(error, 'delete-series', { seriesId });
      setErrors([error?.errors?.[0]?.message || error?.message || 'The series could not be deleted']);
      return false;
    }
  }, [seriesId, fetchItems]);

  /**
   * Moves an item in the displayed order and writes sortKey 10, 20, ... for all
   * items of the series whose sortKey changes.
   */
  const reorderItems = useCallback(async (fromIndex, toIndex) => {
    if (fromIndex === toIndex || isReordering) return;
    const ordered = moveItem(items, fromIndex, toIndex);
    const sortKeys = reorderSortKeys(ordered);
    const updates = sortKeys.filter((entry, index) => ordered[index].sortKey !== entry.sortKey);
    setItems(ordered.map((item, index) => ({ ...item, sortKey: sortKeys[index].sortKey })));
    if (updates.length === 0) return;
    setIsReordering(true);
    try {
      for (let i = 0; i < updates.length; i += REORDER_BATCH_SIZE) {
        await Promise.all(updates.slice(i, i + REORDER_BATCH_SIZE).map((input) => client.graphql({
          query: UPDATE_MEDIA_ITEM_SORT_KEY,
          variables: { input },
        })));
      }
    } catch (error) {
      reportError(error, 'reorder-items', { seriesId, count: updates.length });
      setErrors(['The new order could not be saved completely. The list has been reloaded.']);
      await fetchItems();
    } finally {
      setIsReordering(false);
    }
  }, [items, isReordering, seriesId, fetchItems]);

  const publishedCount = useMemo(() => items.filter((item) => item.status === 'published').length, [items]);

  return {
    series,
    setSeries,
    setField,
    setLocalizedField,
    loadedSlug,
    loadedType,
    items,
    hasItems,
    typeLocked,
    publishedCount,
    industries,
    isLoading,
    isLoadingItems,
    loadError,
    isSaving,
    isReordering,
    errors,
    setErrors,
    loadVersion,
    save,
    deleteSeries,
    reorderItems,
    reloadItems: fetchItems,
  };
}
